import React from 'react';
import styles from './AppShell.module.css';

/**
 * APPSHELL — single source of truth for the app's chrome layout.
 *
 * The grid template lives in `AppShell.module.css`. This component is
 * just a slot host: pass `topBar`, `sideNav`, `aside`, `tabBar`, and
 * the page content as `children`. Anything not provided collapses
 * gracefully (the grid still works because we use min-content sizing).
 *
 * Why slots instead of hard-coded children: the topbar/sidenav need to
 * read the same auth/notification context that page content reads, but
 * they have very different visual responsibilities. Slot composition
 * also makes it trivial to render `<AppShell topBar={null}>` for
 * embed/preview routes that should drop the chrome.
 *
 * Why CSS Modules instead of MUI sx: the grid template uses media
 * queries to swap between mobile/tablet/desktop layouts. Encoding that
 * in `sx` requires a giant nested object that re-renders on every
 * breakpoint match; CSS Modules let the browser do the work.
 */

interface AppShellProps {
  topBar?: React.ReactNode;
  sideNav?: React.ReactNode;
  aside?: React.ReactNode;
  tabBar?: React.ReactNode;
  children: React.ReactNode;
}

const AppShell: React.FC<AppShellProps> = ({
  topBar,
  sideNav,
  aside,
  tabBar,
  children,
}) => {
  return (
    <div className={styles.shell}>
      {topBar && <header className={styles.topbar}>{topBar}</header>}
      {sideNav && <nav className={styles.nav}>{sideNav}</nav>}
      <main className={styles.main}>{children}</main>
      {aside && <aside className={styles.aside}>{aside}</aside>}
      {tabBar && <div className={styles.tabbar}>{tabBar}</div>}
    </div>
  );
};

export default AppShell;
export { styles as appShellStyles };
